import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useToastStore } from '../../store/toastStore';

// --- Types ---

interface Plan {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  price: number;
  currency: 'ARS' | 'USD';
  billing_period: 'monthly' | 'yearly' | 'lifetime';
  max_properties: number;
  max_agents: number;
  max_storage_mb: number;
  max_media_per_property: number;
  features: string[];
  is_highlighted: boolean;
  is_active: boolean;
  sort_order: number;
}

interface PlanListResponse {
  data: Plan[];
}

interface PlanResponse {
  data: Plan;
}

interface PlanFormData {
  name: string;
  slug: string;
  description: string;
  price: number;
  currency: 'ARS' | 'USD';
  billing_period: 'monthly' | 'yearly' | 'lifetime';
  max_properties: number;
  max_agents: number;
  max_storage_mb: number;
  max_media_per_property: number;
  features: string[];
  is_highlighted: boolean;
  sort_order: number;
}

// --- Constants ---

const BILLING_PERIOD_LABEL: Record<string, string> = {
  monthly: 'Mensual',
  yearly: 'Anual',
  lifetime: 'De por vida',
};

const CURRENCY_SYMBOL: Record<string, string> = {
  ARS: '$',
  USD: 'US$',
};

// --- Helpers ---

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function formatPrice(price: number, currency: string): string {
  const symbol = CURRENCY_SYMBOL[currency] || '$';
  return `${symbol}${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getEmptyFormData(): PlanFormData {
  return {
    name: '',
    slug: '',
    description: '',
    price: 0,
    currency: 'ARS',
    billing_period: 'monthly',
    max_properties: 10,
    max_agents: 1,
    max_storage_mb: 500,
    max_media_per_property: 10,
    features: [],
    is_highlighted: false,
    sort_order: 0,
  };
}

// --- Component ---

export default function PlansPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [formData, setFormData] = useState<PlanFormData>(getEmptyFormData());
  const [newFeature, setNewFeature] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const modalRef = useRef<HTMLDialogElement>(null);

  // --- Sync modal ---
  useEffect(() => {
    if (modalOpen) modalRef.current?.showModal();
    else modalRef.current?.close();
  }, [modalOpen]);

  // --- Query ---

  const { data: plansData, isLoading } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: () => api.get<PlanListResponse>('/v1/plans'),
  });

  const plans = (plansData?.data || []).sort((a, b) => a.sort_order - b.sort_order);

  // --- Mutations ---

  const createMutation = useMutation({
    mutationFn: (data: PlanFormData) => api.post<PlanResponse>('/v1/plans', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
      setModalOpen(false);
      addToast('Plan creado correctamente', 'success');
    },
    onError: () => {
      addToast('Error al crear el plan', 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<PlanFormData> }) =>
      api.patch<PlanResponse>(`/v1/plans/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
      setModalOpen(false);
      addToast('Plan actualizado correctamente', 'success');
    },
    onError: () => {
      addToast('Error al actualizar el plan', 'error');
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) => {
      if (is_active) {
        return api.delete<PlanResponse>(`/v1/plans/${id}`);
      }
      return api.patch<PlanResponse>(`/v1/plans/${id}`, { is_active: true });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
      const msg = variables.is_active ? 'Plan desactivado' : 'Plan activado';
      addToast(msg, 'success');
    },
    onError: () => {
      addToast('Error al cambiar el estado del plan', 'error');
    },
  });

  const toggleHighlightMutation = useMutation({
    mutationFn: ({ id, is_highlighted }: { id: number; is_highlighted: boolean }) =>
      api.patch<PlanResponse>(`/v1/plans/${id}`, { is_highlighted: !is_highlighted }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
      addToast('Destacado actualizado', 'success');
    },
    onError: () => {
      addToast('Error al actualizar destacado', 'error');
    },
  });

  // --- Handlers ---

  const handleOpenCreate = (): void => {
    setEditingPlan(null);
    setFormData(getEmptyFormData());
    setNewFeature('');
    setSlugManuallyEdited(false);
    setModalOpen(true);
  };

  const handleOpenEdit = (plan: Plan): void => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      slug: plan.slug,
      description: plan.description || '',
      price: plan.price,
      currency: plan.currency,
      billing_period: plan.billing_period,
      max_properties: plan.max_properties,
      max_agents: plan.max_agents,
      max_storage_mb: plan.max_storage_mb,
      max_media_per_property: plan.max_media_per_property,
      features: [...plan.features],
      is_highlighted: plan.is_highlighted,
      sort_order: plan.sort_order,
    });
    setNewFeature('');
    setSlugManuallyEdited(true);
    setModalOpen(true);
  };

  const handleNameChange = (value: string): void => {
    setFormData((prev) => ({
      ...prev,
      name: value,
      ...(!slugManuallyEdited ? { slug: slugify(value) } : {}),
    }));
  };

  const handleSlugChange = (value: string): void => {
    setSlugManuallyEdited(true);
    setFormData((prev) => ({ ...prev, slug: value }));
  };

  const handleAddFeature = (): void => {
    const trimmed = newFeature.trim();
    if (!trimmed) return;
    setFormData((prev) => ({ ...prev, features: [...prev.features, trimmed] }));
    setNewFeature('');
  };

  const handleRemoveFeature = (index: number): void => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleSave = (): void => {
    if (!formData.name.trim()) {
      addToast('El nombre del plan es obligatorio', 'error');
      return;
    }

    if (editingPlan) {
      updateMutation.mutate({ id: editingPlan.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold">Planes de suscripcion</h1>
          <p className="text-sm text-base-content/60">
            Administra los planes disponibles para las inmobiliarias
          </p>
        </div>
        <button className="btn btn-primary" onClick={handleOpenCreate}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          + Nuevo plan
        </button>
      </div>

      {/* Plans grid */}
      {isLoading ? (
        <div className="flex justify-center p-8">
          <span className="loading loading-spinner loading-lg" />
        </div>
      ) : plans.length === 0 ? (
        <div className="text-center py-12 text-base-content/60">
          <p className="text-lg mb-2">No hay planes configurados</p>
          <p className="text-sm">Crea un nuevo plan para comenzar</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`card bg-base-100 shadow-sm border-l-4 ${
                plan.is_highlighted ? 'border-l-primary' : 'border-l-base-300'
              } ${!plan.is_active ? 'opacity-60' : ''}`}
            >
              <div className="card-body">
                {/* Top badges */}
                <div className="flex items-center gap-2 flex-wrap">
                  {plan.is_highlighted && (
                    <span className="badge badge-primary badge-sm">Destacado</span>
                  )}
                  <span
                    className={`badge badge-sm ${
                      plan.is_active ? 'badge-success' : 'badge-ghost'
                    }`}
                  >
                    {plan.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                  <span className="badge badge-ghost badge-sm">Orden: {plan.sort_order}</span>
                </div>

                {/* Name & price */}
                <h3 className="text-xl font-bold mt-2">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className="text-3xl font-bold text-primary">
                    {formatPrice(plan.price, plan.currency)}
                  </span>
                  <span className="text-base-content/60 text-sm">
                    / {BILLING_PERIOD_LABEL[plan.billing_period] || plan.billing_period}
                  </span>
                </div>

                {/* Description */}
                {plan.description && (
                  <p className="text-sm text-base-content/70 mt-2">{plan.description}</p>
                )}

                {/* Limits 2x2 grid */}
                <div className="divider my-2">Limites</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-base-200/50 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold">{plan.max_properties}</p>
                    <p className="text-xs text-base-content/60">Propiedades</p>
                  </div>
                  <div className="bg-base-200/50 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold">{plan.max_agents}</p>
                    <p className="text-xs text-base-content/60">Agentes</p>
                  </div>
                  <div className="bg-base-200/50 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold">{plan.max_storage_mb}</p>
                    <p className="text-xs text-base-content/60">MB storage</p>
                  </div>
                  <div className="bg-base-200/50 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold">{plan.max_media_per_property}</p>
                    <p className="text-xs text-base-content/60">Media/propiedad</p>
                  </div>
                </div>

                {/* Features */}
                {plan.features.length > 0 && (
                  <>
                    <div className="divider my-2">Funcionalidades</div>
                    <div className="flex flex-wrap gap-1.5">
                      {plan.features.map((feature, i) => (
                        <span key={i} className="badge badge-outline badge-sm gap-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3 w-3 text-success"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                          {feature}
                        </span>
                      ))}
                    </div>
                  </>
                )}

                {/* Action buttons */}
                <div className="card-actions mt-4 pt-2 border-t border-base-200">
                  <button
                    className="btn btn-ghost btn-sm"
                    title="Editar"
                    onClick={() => handleOpenEdit(plan)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                    Editar
                  </button>
                  <button
                    className={`btn btn-ghost btn-sm ${plan.is_highlighted ? 'text-warning' : ''}`}
                    title={plan.is_highlighted ? 'Quitar destacado' : 'Destacar'}
                    onClick={() =>
                      toggleHighlightMutation.mutate({
                        id: plan.id,
                        is_highlighted: plan.is_highlighted,
                      })
                    }
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill={plan.is_highlighted ? 'currentColor' : 'none'}
                      stroke="currentColor"
                      strokeWidth={plan.is_highlighted ? 0 : 1.5}
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    {plan.is_highlighted ? 'Destacado' : 'Destacar'}
                  </button>
                  <button
                    className={`btn btn-ghost btn-sm ${plan.is_active ? 'text-error' : 'text-success'}`}
                    title={plan.is_active ? 'Desactivar' : 'Activar'}
                    onClick={() =>
                      toggleActiveMutation.mutate({
                        id: plan.id,
                        is_active: plan.is_active,
                      })
                    }
                  >
                    {plan.is_active ? (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                        </svg>
                        Desactivar
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Activar
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <dialog ref={modalRef} className="modal" onClose={() => setModalOpen(false)}>
        <div className="modal-box max-w-2xl">
          <h3 className="font-bold text-lg mb-4">
            {editingPlan ? `Editar plan: ${editingPlan.name}` : 'Nuevo plan'}
          </h3>

          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            {/* Name */}
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text">Nombre *</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                placeholder="Ej: Profesional"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
              />
            </div>

            {/* Slug */}
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text">Slug</span>
                <span className="label-text-alt text-base-content/50">
                  Generado automaticamente del nombre
                </span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                placeholder="profesional"
                value={formData.slug}
                onChange={(e) => handleSlugChange(e.target.value)}
              />
            </div>

            {/* Description */}
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text">Descripcion</span>
              </label>
              <textarea
                className="textarea textarea-bordered w-full h-20"
                placeholder="Descripcion del plan..."
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              />
            </div>

            {/* Price + Currency + Billing period */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">Precio</span>
                </label>
                <input
                  type="number"
                  className="input input-bordered w-full"
                  min={0}
                  step={0.01}
                  value={formData.price}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, price: parseFloat(e.target.value) || 0 }))
                  }
                />
              </div>

              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">Moneda</span>
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className={`btn flex-1 ${formData.currency === 'ARS' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setFormData((prev) => ({ ...prev, currency: 'ARS' }))}
                  >
                    ARS
                  </button>
                  <button
                    type="button"
                    className={`btn flex-1 ${formData.currency === 'USD' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setFormData((prev) => ({ ...prev, currency: 'USD' }))}
                  >
                    USD
                  </button>
                </div>
              </div>

              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">Periodo de facturacion</span>
                </label>
                <select
                  className="select select-bordered w-full"
                  value={formData.billing_period}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      billing_period: e.target.value as PlanFormData['billing_period'],
                    }))
                  }
                >
                  <option value="monthly">Mensual</option>
                  <option value="yearly">Anual</option>
                  <option value="lifetime">De por vida</option>
                </select>
              </div>
            </div>

            {/* Limits */}
            <div className="divider">Limites</div>

            <div className="grid grid-cols-2 gap-4">
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">Max propiedades</span>
                </label>
                <input
                  type="number"
                  className="input input-bordered w-full"
                  min={0}
                  value={formData.max_properties}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      max_properties: parseInt(e.target.value) || 0,
                    }))
                  }
                />
              </div>

              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">Max agentes</span>
                </label>
                <input
                  type="number"
                  className="input input-bordered w-full"
                  min={0}
                  value={formData.max_agents}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      max_agents: parseInt(e.target.value) || 0,
                    }))
                  }
                />
              </div>

              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">Max storage (MB)</span>
                </label>
                <input
                  type="number"
                  className="input input-bordered w-full"
                  min={0}
                  value={formData.max_storage_mb}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      max_storage_mb: parseInt(e.target.value) || 0,
                    }))
                  }
                />
              </div>

              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">Max media por propiedad</span>
                </label>
                <input
                  type="number"
                  className="input input-bordered w-full"
                  min={0}
                  value={formData.max_media_per_property}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      max_media_per_property: parseInt(e.target.value) || 0,
                    }))
                  }
                />
              </div>
            </div>

            {/* Features */}
            <div className="divider">Funcionalidades</div>

            <div className="form-control w-full">
              <div className="flex gap-2">
                <input
                  type="text"
                  className="input input-bordered flex-1"
                  placeholder="Agregar funcionalidad..."
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddFeature();
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn btn-outline btn-primary"
                  onClick={handleAddFeature}
                >
                  Agregar
                </button>
              </div>
            </div>

            {formData.features.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.features.map((feature, i) => (
                  <span key={i} className="badge badge-lg gap-2">
                    {feature}
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs p-0"
                      onClick={() => handleRemoveFeature(i)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Highlighted + Sort order */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
              <div className="form-control">
                <label className="label cursor-pointer gap-3">
                  <span className="label-text">Destacar plan</span>
                  <input
                    type="checkbox"
                    className="toggle toggle-primary"
                    checked={formData.is_highlighted}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, is_highlighted: e.target.checked }))
                    }
                  />
                </label>
              </div>

              <div className="form-control flex-1">
                <label className="label">
                  <span className="label-text">Orden</span>
                </label>
                <input
                  type="number"
                  className="input input-bordered w-full sm:w-32"
                  value={formData.sort_order}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      sort_order: parseInt(e.target.value) || 0,
                    }))
                  }
                />
              </div>
            </div>
          </div>

          <div className="modal-action">
            <button type="button" className="btn" onClick={() => setModalOpen(false)}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={isSaving}
              onClick={handleSave}
            >
              {isSaving ? (
                <span className="loading loading-spinner loading-sm" />
              ) : (
                'Guardar'
              )}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setModalOpen(false)}>close</button>
        </form>
      </dialog>
    </div>
  );
}
